// frontend/src/pages/styleGuide/StyleGuide.jsx

import React, { useMemo, useState, useContext } from "react";
import { Navigate } from "react-router-dom";
import "./styleGuide.css";
import { STYLE_GUIDE_LAST_UPDATED, STYLE_GUIDE_SECTIONS } from "./StyleGuideContent";
import { Context } from "../../context/Context";

export default function StyleGuide() {
  const { user } = useContext(Context);

  const sections = useMemo(() => STYLE_GUIDE_SECTIONS, []);
  const [open, setOpen] = useState(() => Object.fromEntries(sections.map((s) => [s.title, false])));

  // Writers (role >= 2) and above only
  if (!user || user.role < 2) {
    return <Navigate to="/" replace />;
  }

  const expandAll = () => setOpen(Object.fromEntries(sections.map((s) => [s.title, true])));
  const collapseAll = () => setOpen(Object.fromEntries(sections.map((s) => [s.title, false])));

  const toggle = (title) => setOpen((prev) => ({ ...prev, [title]: !prev[title] }));

  const getSectionTitleByAnchorId = (anchorId) => {
    const hasAnchorInBlocks = (blocks = []) =>
      blocks.some((b) => b?.id === anchorId || hasAnchorInBlocks(b?.children));

    const match = sections.find((s) => {
      const sectionId = `sg-${s.title.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`;
      return sectionId === anchorId || hasAnchorInBlocks(s.blocks);
    });

    return match?.title || null;
  };

  const renderListItems = (items) =>
    items.map((item, i) => {
      if (typeof item === "string") {
        return (
          <li key={i} style={{ whiteSpace: "pre-line" }}>
            {item}
          </li>
        );
      }

      return (
        <li key={i} style={{ whiteSpace: "pre-line" }}>
          {item.text}
          {item.children && (
            <ol className="nested-list" type="a">
              {renderListItems(item.children)}
            </ol>
          )}
        </li>
      );
    });

  const renderBlock = (b, idx) => {
    if (b.type === "p") {
      if (b.link) {
        return (
          <p key={idx}>
            <a href={b.text} target="_blank" rel="noreferrer">
              {b.text}
            </a>
          </p>
        );
      }

      if (b.boldLabel) {
        const label = b.boldLabel;
        const rest = b.text.startsWith(label) ? b.text.slice(label.length).trimStart() : b.text;
        return (
          <p key={idx}>
            <strong>{label}</strong> {rest}
          </p>
        );
      }

      if (b.bold) {
        return (
          <p key={idx}>
            <strong>{b.text}</strong>
          </p>
        );
      }

      if (b.anchorTo) {
        return (
          <p key={idx}>
            {b.label ? `${b.label} ` : null}
            <a
              href={`#${b.anchorTo}`}
              onClick={(e) => {
                e.preventDefault();

                // Expand the target accordion section (title must match your section.title)
                // For this specific case, it’s in the "Appendices" section.
                const targetTitle = getSectionTitleByAnchorId(b.anchorTo);
                if (targetTitle) setOpen((prev) => ({ ...prev, [targetTitle]: true }));

                // Scroll after state update has a chance to render
                setTimeout(() => {
                  const el = document.getElementById(b.anchorTo);
                  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
                  window.history.replaceState(null, "", `#${b.anchorTo}`);
                }, 0);
              }}
            >
              {b.linkText ?? b.text}
            </a>
          </p>
        );
      }

      return <p key={idx}>{b.text}</p>;
    }

    if (b.type === "h3") return <h3 key={idx} id={b.id || undefined}>{b.text}</h3>;

    if (b.type === "h4") return <h4 key={idx} id={b.id || undefined}>{b.text}</h4>;

    if (b.type === "checklist") {
      return (
        <ul key={idx} className="style-guide-checklist">
          {b.items.map((it, j) => {
            // Supports either strings OR objects like { text, checked }
            const text = typeof it === "string" ? it : it.text;
            const checked = typeof it === "string" ? false : !!it.checked;

            return (
              <li key={j} className="style-guide-checklist-item">
                <input type="checkbox" checked={checked} readOnly aria-hidden="true" tabIndex={-1} />
                <span  className="style-guide-checklist-text">{text}</span>
              </li>
            );
          })}
        </ul>
      );
    }

    if (b.type === "ul") {
      return <ul key={idx}>{renderListItems(b.items)}</ul>;
    }

    if (b.type === "ol") {
      return <ol key={idx}>{renderListItems(b.items)}</ol>;
    }

    if (b.type === "table") {
      return (
        <div key={idx} className="style-guide-table-wrap">
          <table className="style-guide-table">
            <thead>
              <tr>
                {b.headers.map((h, j) => (
                  <th key={j}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {b.rows.map((row, rIdx) => (
                <tr key={rIdx}>
                  {row.map((cell, cIdx) => (
                    <td key={cIdx}>{cell}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }

    return null;
  };

  return (
    <div className="style-guide-container">
      <section className="style-guide-hero">
        <h1>FLOW Style Guide</h1>
        <p className="style-guide-updated">Last Updated: {STYLE_GUIDE_LAST_UPDATED}</p>

        <div className="style-guide-controls">
          <button type="button" className="style-guide-control-btn" onClick={expandAll}>
            Expand all
          </button>
          <button type="button" className="style-guide-control-btn" onClick={collapseAll}>
            Collapse all
          </button>
        </div>
      </section>

      <div className="style-guide-accordion">
        {sections.map((section) => {
          const isOpen = !!open[section.title];
          const sectionId = `sg-${section.title.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`;

          return (
            <section id={sectionId} className="style-guide-section" key={section.title}>
              <button
                type="button"
                className="style-guide-section-header"
                onClick={() => toggle(section.title)}
                aria-expanded={isOpen}
                aria-controls={sectionId}
              >
                <h2 className="style-guide-section-title">{section.title}</h2>
                <span className="style-guide-section-toggle" aria-hidden="true">
                  {isOpen ? "−" : "+"}
                </span>
              </button>

              <div id={sectionId} className={`style-guide-section-body ${isOpen ? "open" : "closed"}`}>
                {section.blocks.map(renderBlock)}
              </div>
            </section>
          );
        })}
      </div>
    </div>
  );
}
